import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import {
	ListingPhotoKey,
	PhotoManagerResource,
	PhotoResource,
} from "api/resource/listing-photo";
import { arrayToById } from "redux/normalize";
import { loginReset } from "./authentication";

/**
 * Shared listing photo manager (MAX-3898) — backs the <PhotoManager> component reused by every
 * listing editor. Only one listing's photo manager is ever open at a time, and every mutating
 * endpoint returns the full PhotoManagerResource, so state is a single flat snapshot rather than a
 * multi-listing normalized store: `listingPhotosLoaded` always replaces mainPhotoUrl/photoYn/
 * canRevertToMls/byId wholesale. `resetListingPhotos` clears it when the manager dialog closes so a
 * different listing's photos never leak into the next open.
 */
export interface ListingPhotosState {
	loading?: boolean;
	error?: string | null;
	loaded: boolean;
	mainPhotoUrl: string | null;
	photoYn: boolean;
	canRevertToMls: boolean;
	byId: {
		[propertyPhotoId: number]: PhotoResource;
	};
}

const initialState: ListingPhotosState = {
	loaded: false,
	mainPhotoUrl: null,
	photoYn: false,
	canRevertToMls: false,
	byId: {},
};

const fetchListingPhotos = createAsyncThunk(
	"listingPhotos/fetch",
	async (payload: { key: ListingPhotoKey }, thunkAPI) => {
		thunkAPI.dispatch(listingPhotosLoading());
		const result = api.getListingPhotos(payload.key);
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

// Two thunks over the same add-photo endpoint (mainPhoto: false vs true) — kept separate rather than
// a single thunk with a flag so each call site's intent (extra photo vs "make main on add") is explicit.
const addExtraPhoto = createAsyncThunk(
	"listingPhotos/addExtra",
	async (payload: { key: ListingPhotoKey; photoUrl: string }, thunkAPI) => {
		const result = api.addListingPhoto(payload.key, { photoUrl: payload.photoUrl, mainPhoto: false });
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

const addAsMainPhoto = createAsyncThunk(
	"listingPhotos/addAsMain",
	async (payload: { key: ListingPhotoKey; photoUrl: string }, thunkAPI) => {
		const result = api.addListingPhoto(payload.key, { photoUrl: payload.photoUrl, mainPhoto: true });
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

const reorderPhotos = createAsyncThunk(
	"listingPhotos/reorder",
	async (payload: { key: ListingPhotoKey; orderedIds: number[] }, thunkAPI) => {
		const result = api.reorderListingPhotos(payload.key, { orderedIds: payload.orderedIds });
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

// Delete (endpoint #4) returns 204 with no resource, unlike every other mutating endpoint. Rather
// than guess how the server recomputes mainPhotoUrl/photoYn when the deleted photo was the main
// one, refetch the manager after the delete succeeds so those flags stay server-accurate.
const deletePhoto = createAsyncThunk(
	"listingPhotos/delete",
	async (payload: { key: ListingPhotoKey; propertyPhotoId: number }, thunkAPI) => {
		await api.deleteListingPhoto(payload.propertyPhotoId, payload.key);
		const result = api.getListingPhotos(payload.key);
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

const makeMainPhoto = createAsyncThunk(
	"listingPhotos/makeMain",
	async (payload: { key: ListingPhotoKey; propertyPhotoId: number }, thunkAPI) => {
		const result = api.makeMainListingPhoto(payload.key, { propertyPhotoId: payload.propertyPhotoId });
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

const revertPhotosToMls = createAsyncThunk(
	"listingPhotos/revert",
	async (payload: { key: ListingPhotoKey }, thunkAPI) => {
		const result = api.revertListingPhotosToMls(payload.key);
		result
			.then((response) => thunkAPI.dispatch(listingPhotosLoaded(response)))
			.catch((error) => thunkAPI.dispatch(listingPhotosFailed(error)));
		return result;
	}
);

export const listingPhotosSlice = createSlice({
	name: "listingPhotos",
	initialState,
	reducers: {
		listingPhotosLoading(state) {
			state.loading = true;
		},
		listingPhotosLoaded(state, action: PayloadAction<PhotoManagerResource>) {
			state.loading = false;
			state.error = null;
			state.loaded = true;
			state.mainPhotoUrl = action.payload.mainPhotoUrl;
			state.photoYn = action.payload.photoYn;
			state.canRevertToMls = action.payload.canRevertToMls;
			state.byId = arrayToById(action.payload.photos, "propertyPhotoId");
		},
		listingPhotosReset(state) {
			return { ...initialState, error: state.error };
		},
		listingPhotosFailed(state, action: PayloadAction<any>) {
			state.loading = false;
			state.error = action.payload && action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	listingPhotosLoading,
	listingPhotosLoaded,
	listingPhotosReset,
	listingPhotosFailed,
} = listingPhotosSlice.actions;

export {
	fetchListingPhotos,
	addExtraPhoto,
	addAsMainPhoto,
	reorderPhotos,
	deletePhoto,
	makeMainPhoto,
	revertPhotosToMls,
};
